import React, { useState, useEffect } from 'react';
import { 
    Box, MenuItem, TextField, Button, Typography, Chip, Stack, 
    Alert, IconButton, Tooltip, ToggleButton, ToggleButtonGroup, Avatar 
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import RefreshIcon from '@mui/icons-material/Refresh';
import StadiumIcon from '@mui/icons-material/Stadium'; // Icono Cancha
import PersonIcon from '@mui/icons-material/Person';   // Icono Persona

// --- CONTROLADORES ---
import { getAllAssets } from '../../../../controllers/company/AssetsController'; 
import { GetAll as getAllEmployees } from '../../../../controllers/company/AssociatesController'; // <--- NUEVO
import { 
    getResourcesByProduct, 
    assignResourceToProduct, 
    removeResourceFromProduct 
} from '../../../../controllers/company/products/ProductResourceController';

// --- COMPONENTES UI ---
import QUDProgress from '../../../../components/progress';
import ResponseComponent from '../../../../components/responseComponent';

export default function ResourceLinker({ productId }) {
  // Datos
  const [myAssets, setMyAssets] = useState([]); 
  const [myEmployees, setMyEmployees] = useState([]); // <--- NUEVO
  const [assignedResources, setAssignedResources] = useState([]); 

  // UI Selection
  const [resourceType, setResourceType] = useState('asset'); // 'asset' o 'employee'
  const [selectedId, setSelectedId] = useState(''); // ID del recurso seleccionado
  
  // Feedback
  const [isLoading, setIsLoading] = useState(false);
  const [responseState, setResponseState] = useState({ show: false, success: false, title: '', message: '' });

  useEffect(() => {
    if(productId) loadData();
  }, [productId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
        // Carga paralela: Activos, Empleados y Asignaciones actuales
        const [assetsRes, employeesRes, assignedRes] = await Promise.all([
            getAllAssets(),
            getAllEmployees(),
            getResourcesByProduct(productId)
        ]);

        if(assetsRes.success) setMyAssets(assetsRes.assets || []);
        
        // Ajusta según cómo devuelva la data tu API de business cards (ej: data.data o data)
        console.log('RES ASSETS ===> ',employeesRes)
        if(employeesRes.success) setMyEmployees(employeesRes.data || []); 
        
        if(assignedRes.success) setAssignedResources(assignedRes.data || []);

    } catch (e) {
        console.error("Error cargando recursos:", e);
    }
    setIsLoading(false);
  };

  const handleAssign = async () => {
    if (!selectedId) return;
    
    setIsLoading(true);
    // Enviamos el tipo dinámico ('asset' o 'employee')
    const res = await assignResourceToProduct(productId, selectedId, resourceType);
    setIsLoading(false);

    if (res.success) {
        setSelectedId(''); 
        loadData(); 
    } else {
        setResponseState({
            show: true, success: false, title: 'Error',
            message: res.message || 'No se pudo asignar.'
        });
    }
  };

  const handleRemove = async (assignmentId) => {
    if(!window.confirm("¿Desvincular?")) return;
    setIsLoading(true);
    const res = await removeResourceFromProduct(assignmentId);
    setIsLoading(false);
    if(res.success) loadData();
    else setResponseState({ show: true, success: false, title: 'Error', message: res.message });
  };

  // Helper para renderizar el nombre correcto en el Chip
  const getResourceLabel_OLD = (res) => {
      if (res.asset) return `🏟️ ${res.asset.name}`;
      if (res.business_card) return `👤 ${res.business_card.name} ${res.business_card.last_name || ''}`;
      return 'Desconocido';
  };

  const getResourceLabel = (res) => {
        
      if (res.asset) return `🏟️ ${res.standard_name}`;
      if (res.business_card) return `👤 ${res.standard_name}`;
      return 'Desconocido';
  };

  return (
    <Box sx={{ mt: 0, p: 3, border: '1px solid #e0e0e0', borderRadius: 2, bgcolor: '#fafafa' }}>
      <QUDProgress open={isLoading} />
      <ResponseComponent show={responseState.show} success={responseState.success} title={responseState.title} message={responseState.message} actionText="Ok" onClick={() => setResponseState({ ...responseState, show: false })} />

      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
            <LinkIcon sx={{verticalAlign: 'middle', mr: 1, fontSize: 20}}/>
            Recursos Asignados
        </Typography>
        <Tooltip title="Recargar"><IconButton size="small" onClick={loadData}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      {/* LISTA DE ASIGNADOS */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        {assignedResources.length > 0 ? (
            assignedResources.map((res) => (
            <Chip 
                key={res.id} 
                label={getResourceLabel(res)}
                onDelete={() => handleRemove(res.id)} 
                //color={res.type === 'asset' ? 'primary' : 'secondary'} // Diferenciar color por tipo
                variant="outlined"
                sx={{ bgcolor: 'white' }}
            />
            ))
        ) : (
            <Alert severity="info" sx={{ width: '100%', py: 0 }}>Sin recursos asignados.</Alert>
        )}
      </Stack>

      {/* SELECTOR DE TIPO (SWITCH) */}
      <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 2, border: '1px solid #eee' }}>
          <Typography variant="caption" display="block" mb={1} fontWeight="bold">Asignar Nuevo Recurso:</Typography>
          
          <ToggleButtonGroup
            value={resourceType}
            exclusive
            onChange={(e, newType) => {
                if (newType) {
                    setResourceType(newType);
                    setSelectedId(''); // Limpiar selección al cambiar tipo
                }
            }}
            size="small"
            sx={{ mb: 2, width: '100%' }}
          >
            <ToggleButton value="asset" sx={{ flex: 1 }}>
                <StadiumIcon sx={{ mr: 1, fontSize: 18 }} /> Activo Físico
            </ToggleButton>
            <ToggleButton value="employee" sx={{ flex: 1 }}>
                <PersonIcon sx={{ mr: 1, fontSize: 18 }} /> Empleado
            </ToggleButton>
          </ToggleButtonGroup>

          {/* SELECTOR DINÁMICO */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              label={resourceType === 'asset' ? "Seleccionar Cancha/Sala" : "Seleccionar Profesional"}
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              fullWidth
              size="small"
            >
              {resourceType === 'asset' ? (
                  myAssets.length > 0 ? (
                      myAssets.map((asset) => (
                        <MenuItem key={asset.id} value={asset.id}>{asset.name}</MenuItem>
                      ))
                  ) : <MenuItem disabled>No hay activos creados</MenuItem>
              ) : (
                  myEmployees.length > 0 ? (
                      myEmployees.map((emp) => (
                        <MenuItem key={emp.id} value={emp.id}>
                            {emp.person.name} {emp.person.last_name}
                        </MenuItem>
                      ))
                  ) : <MenuItem disabled>No hay empleados registrados</MenuItem>
              )}
            </TextField>
            
            <Button variant="contained" onClick={handleAssign} disabled={!selectedId}>
              Asignar
            </Button>
          </Box>
      </Box>
    </Box>
  );
}